import { FieldPath, FieldValues, RegisterOptions, useController } from "react-hook-form";
import { Input, type TInputStandardProps } from "../input/input";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "./hook-form-helper";
import { THookFormFieldProps } from "./hook-form-types";
import { useEffect } from "react";

export type THookFormInputRules<T extends FieldValues = FieldValues> = Omit<
  RegisterOptions<T, FieldPath<T>>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

type THookFormInputFieldProps<TFieldValues extends FieldValues> = THookFormFieldProps<TFieldValues> & {
  rules?: THookFormInputRules<TFieldValues>;
} & Pick<
    TInputStandardProps,
    "label" | "helperText" | "className" | "placeholder" | "inputMode" | "autoComplete" | "isReadOnly" | "isDisabled"
  >;

export const HookFormInputField = <T extends FieldValues>({
  rules,
  control,
  name,
  label,
  className,
  helperText,
  placeholder,
  autoComplete,
  inputMode,
  isReadOnly,
  isDisabled,
  defaultValue,
}: THookFormInputFieldProps<T>) => {
  const { t } = useTranslation();

  const { field, fieldState } = useController({
    name: name,
    control: control,
    defaultValue: defaultValue,
    disabled: isDisabled,
    rules: rules,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (defaultValue) {
      field.onChange(defaultValue);
    }
  }, []);

  const { invalid, error } = fieldState;

  return (
    <Input.Text
      className={className}
      label={label}
      onChange={field.onChange}
      onBlur={field.onBlur}
      name={field.name}
      value={field.value}
      fieldRef={field.ref}
      isValid={!invalid}
      isRequired={rules?.required ? true : false}
      errorText={getErrorMessage(t, error)}
      helperText={helperText}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={inputMode}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
    />
  );
};
